/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, inject, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { assets } from "../../core/constants/assets.const";
import {  DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { TableModule } from "primeng/table";
import { Checkbox } from "primeng/checkbox";
import { MultiSelectModule } from "primeng/multiselect";
import { CommonModule } from "@angular/common";
import { ASSIGN_TYPE, LIST_TYPE } from "../../core/constants/keys.const";
import { AssignmentType, Department, TableRow } from "../../models/Member.model";
import { BusinessMemberService } from "../../service/businessMember.service";
import { AuthService } from "../../service/auth.service";
import { CalendarModule } from "primeng/calendar";
import { DropdownModule } from "primeng/dropdown";
import { CartService } from "../../service/cart.service";
import { RadioButtonModule } from "primeng/radiobutton";

interface PurchaseDetail {
  _id: string;
  seats: number;
  seatsFilled: number;
  expiryDate: string;
  practiceSet: {
    _id: string;
    title: string;
    description: string;
    price: number;
    totalQuestions: number;
  };
  subjectDetails: {
    _id: string;
    name: string;
  }[];
}

@Component({
  selector: "notchmark-choose-members",
  imports: [
    ButtonModule,
    InputTextModule,
    FormsModule,
    TableModule,
    Checkbox,
    MultiSelectModule,
    CommonModule,
    CalendarModule,
    DropdownModule,
    RadioButtonModule
  ],
  templateUrl: "./choose-members.component.html",
  styleUrl: "./choose-members.component.scss"
})
export class ChooseMembersComponent implements OnInit {
  assetsConst = assets;
  practiceSetId = '';
  purchaseDetailId = '';
  currentStep = 1;

  private _settingsService = inject(AuthService);
  private _businessMemberService = inject(BusinessMemberService);
  private cartService = inject(CartService);
  purchaseList: PurchaseDetail[] = [];
  selectedPurchase: PurchaseDetail | null = null;
  assignmentTypes: AssignmentType[] = [
    { name: "All", value: "all" },
    { name: "Department", value: "department" },
    { name: "Manual", value: "manual" },
  ];
  selectedAssignmentType = "all";
  selectedDepartments: string[] = [];
  startDate: Date | null = null;
  endDate: Date | null = null;
  selectAll = false;

  tableData: TableRow[] = [];
  departments: Department[] = [];
  filteredTableData: TableRow[] = [];

  private ref = inject(DynamicDialogRef);
  public config = inject(DynamicDialogConfig);

  ngOnInit() {
    this.loadPurchaseList();
  }

  loadPurchaseList() {
    const query = '';
    this.cartService.purchaseList(query).subscribe({
      next: (response) => {
        if (response && response.length > 0 && response[0].list) {
          this.purchaseList = response[0].list;
        }
      },
      error: (error) => {
        console.error('Error loading purchase list:', error);
      }
    });
  }

  proceedToMemberSelection() {
    if (!this.selectedPurchase) {
      return;
    }
    this.purchaseDetailId = this.selectedPurchase._id;
    this.practiceSetId = this.selectedPurchase.practiceSet._id;
    const availableSeats = this.selectedPurchase.seats - this.selectedPurchase.seatsFilled;
    if (availableSeats <= 0) {
      alert('No available seats for this purchase');
      return;
    }
    this.currentStep = 2;
    this.loadMember();
    this.loadDepartments();
  }

  backToPurchaseSelection() {
    this.currentStep = 1;
    this.tableData = [];
    this.filteredTableData = [];
    this.selectedAssignmentType = 'all';
    this.selectedDepartments = [];
    this.startDate = null;
    this.endDate = null;
  }

  loadMember() {
    const query = `type=${LIST_TYPE.MEMBERS}&practiceSetId=${this.practiceSetId}`;
    this._settingsService.getSettingsData(query).subscribe({
      next: (response) => {
        this.tableData = response.map((member: any) => ({
          id: member._id,
          name: member.fullName,
          email: member.email,
          department: member.departmentId?.name || "N/A",
          departmentId: member.departmentId?._id || "",
          selected: false,
        }));
        this.filteredTableData = [...this.tableData];
        this.filterTableData();
      },
    });
  }

  loadDepartments() {
    const query = `type=${LIST_TYPE.DEPARTMENTS}&practiceSetId=${this.practiceSetId}`;
    this._settingsService.getSettingsData(query).subscribe({
      next: (response) => {
        this.departments = response.map((dept: any) => ({
          name: dept.name,
          value: dept._id || dept.name.toLowerCase(),
        }));
      },
    });
  }

  private getAssignType(): number {
    switch (this.selectedAssignmentType) {
      case 'all':
        return ASSIGN_TYPE.ALL;
      case 'department':
        return ASSIGN_TYPE.DEPARTMENT;
      case 'manual':
        return ASSIGN_TYPE.MANUALLY;
      default:
        return ASSIGN_TYPE.ALL;
    }
  }

  private buildAssignPayload(): any {
    const assignType = this.getAssignType();
    const payload: any = {
      assignType: assignType,
      purchaseDetailId: this.purchaseDetailId,
      practiceSetId: this.practiceSetId,
    };

    if (this.startDate) {
      payload.startDate = this.startDate.toISOString();
    }
    if (this.endDate) {
      payload.endDate = this.endDate.toISOString();
    }

    if (assignType === ASSIGN_TYPE.DEPARTMENT) {
      if (this.selectedDepartments && this.selectedDepartments.length > 0) {
        payload.departmentIds = this.selectedDepartments;
      }
    } else if (assignType === ASSIGN_TYPE.MANUALLY) {
      const selectedMemberIds = this.filteredTableData
        .filter((row) => row.selected)
        .map((row) => row.id);

      if (selectedMemberIds.length > 0) {
        payload.businessMemberId = selectedMemberIds;
      }
    }

    return payload;
  }

  closeDialog() {
    this.ref.close();
  }

  onAssignmentTypeChange() {
    this.selectedDepartments = [];
    this.filterTableData();
  }

  filterByDepartment() {
    this.filterTableData();
  }

  filterTableData() {
    if (this.selectedAssignmentType === "all") {
      this.filteredTableData = [...this.tableData];
      this.filteredTableData.forEach((row) => (row.selected = true));
      this.selectAll = true;
    } else if (this.selectedAssignmentType === "department") {
      if (this.selectedDepartments.length > 0) {
        this.filteredTableData = this.tableData.filter((row) =>
          this.selectedDepartments.includes(row.departmentId)
        );
        this.filteredTableData.forEach((row) => (row.selected = true));
        this.selectAll = true;
      } else {
        this.filteredTableData = [...this.tableData];
        this.filteredTableData.forEach((row) => (row.selected = false));
        this.selectAll = false;
      }
    } else if (this.selectedAssignmentType === "manual") {
      this.filteredTableData = [...this.tableData];
      this.filteredTableData.forEach((row) => (row.selected = false));
      this.selectAll = false;
    } else {
      this.filteredTableData = [...this.tableData];
      this.filteredTableData.forEach((row) => (row.selected = false));
      this.selectAll = false;
    }
  }

  toggleSelectAll(event: any) {
    this.filteredTableData.forEach((row) => (row.selected = event.checked));
  }

  hasSelectedMembers(): boolean {
    if (this.selectedAssignmentType === "all") {
      return this.filteredTableData.length > 0;
    }
    if (this.selectedAssignmentType === "department") {
      return this.selectedDepartments.length > 0;
    }
    return this.filteredTableData.some((row) => row.selected);
  }

  assignSeats() {
    if (this.selectedPurchase) {
      const availableSeats = this.selectedPurchase.seats - this.selectedPurchase.seatsFilled;
      const selectedCount = this.getSelectedMembersCount();

      if (selectedCount > availableSeats) {
        alert(`You can only assign ${availableSeats} seats. You have selected ${selectedCount} members.`);
        return;
      }
    }

    const selectedMembers = this.filteredTableData.filter((row) => row.selected);
    const payload = this.buildAssignPayload();

    this._businessMemberService.assignMembers(payload).subscribe({
      next: (response) => {
        const assignmentData = {
          assignmentType: this.selectedAssignmentType,
          departments: this.selectedDepartments,
          startDate: this.startDate,
          endDate: this.endDate,
          selectedMembers: selectedMembers,
          selectedPurchase: this.selectedPurchase,
          apiResponse: response,
        };
        this.ref.close(assignmentData);
      },
    });
  }

  private getSelectedMembersCount(): number {
    if (this.selectedAssignmentType === 'all') {
      return this.filteredTableData.length;
    } else if (this.selectedAssignmentType === 'department') {
      return this.filteredTableData.filter(row => 
        this.selectedDepartments.includes(row.departmentId)
      ).length;
    } else {
      return this.filteredTableData.filter(row => row.selected).length;
    }
  }
}
